import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Download, Loader2, MapPin, CheckCircle2 } from "lucide-react";
import { Avatar, Card, CardTitle, Logo, StatusBadge } from "@/components/common";
import { LineItems, PaymentSchedule, QuoteTerms } from "@/components/TripSections";
import { fmtDate, fmtMoney, itinerary, paymentSchedule, returnDate, tripTotal } from "@/lib/trip";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicQuote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [confirm, setConfirm] = useState(false);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/quote-data/${token}`)
      .then(({ data }) => { setData(data); setState("ready"); })
      .catch(() => setState("error"));
  }, [token]);

  if (state === "loading") return <div className="grid min-h-screen place-items-center bg-[#F5F7FA]"><Loader2 className="h-6 w-6 animate-spin text-navy-700" /></div>;
  if (state === "error") return (
    <div className="grid min-h-screen place-items-center bg-[#F5F7FA] px-6 text-center">
      <div>
        <p className="font-display text-xl font-bold text-navy-900">Quote not found</p>
        <p className="mt-2 text-sm text-slate-500">This quote link may have expired. Please contact your advisor for an updated link.</p>
      </div>
    </div>
  );

  const t = data.trip;
  const advisor = data.advisor || {};
  const advisorAvatar = advisor.avatar && advisor.id ? `${BACKEND}/api/public/agents/${advisor.id}/avatar` : null;
  const deposit = paymentSchedule(t)[0]?.amount || 0;

  const approve = async () => {
    if (!confirm) { setConfirm(true); return; }
    setApproving(true);
    try {
      const { data: r } = await axios.post(`${BACKEND}/api/public/quotes/${token}/approve`);
      setData((d) => ({ ...d, trip: { ...d.trip, status: r.status || "BOOKED", bookingNumber: r.bookingNumber } }));
    } catch {
      setApproving(false);
      setConfirm(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] py-6 sm:py-10" data-testid="public-quote">
      <div className="mx-auto mb-4 flex max-w-4xl items-center justify-between px-4 print:hidden">
        <p className="text-xs text-slate-500">Prepared by {data.agencyName}</p>
        <button onClick={() => window.print()} data-testid="public-quote-print" className="inline-flex h-10 items-center gap-2 rounded-full bg-navy-900 px-5 text-sm font-semibold text-white transition-colors hover:bg-navy-800">
          <Download className="h-4 w-4" /> Download / Print
        </button>
      </div>

      <Card id="quote-printable" className="mx-auto max-w-4xl overflow-hidden">
        <div className="flex flex-col gap-6 border-b border-slate-100 p-6 sm:flex-row sm:items-start sm:justify-between sm:p-8">
          <div>
            <Logo brand="mytrip" sub="Powered by Topsail Travel" />
            <div className="mt-4 flex items-center gap-3">
              <Avatar name={advisor.name} src={advisorAvatar} size="md" data-testid="public-quote-advisor-photo" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-navy-900">{advisor.name}</p>
                <p className="truncate text-xs text-slate-500">{[advisor.title, advisor.email].filter(Boolean).join(" · ")}</p>
              </div>
            </div>
          </div>
          <div className="sm:text-right">
            <div className="flex items-center gap-2 sm:justify-end"><p className="font-mono text-sm text-navy-900" data-testid="public-quote-number">{t.quoteNumber}</p><StatusBadge status={t.status} /></div>
            <p className="mt-2 text-xs text-slate-500">Prepared {fmtDate(t.createdOn || t.bookedOn)}</p>
          </div>
        </div>

        <div className="relative h-44 sm:h-56">
          <img src={t.image} alt={t.destination} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 to-transparent" />
          <div className="absolute bottom-5 left-6 right-6 text-white sm:left-8">
            <p className="text-xs text-white/70">Prepared for {data.clientName}</p>
            <h1 className="font-display text-2xl font-bold sm:text-3xl" data-testid="public-quote-title">{t.title}</h1>
            <p className="text-sm text-white/80">{t.vessel} · {fmtDate(t.departure)} – {fmtDate(returnDate(t))}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 p-6 sm:p-8 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Invoice</h2>
            <LineItems trip={t} />
            <h2 className="mb-2 mt-8 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Itinerary Overview</h2>
            <ol className="space-y-2" data-testid="public-quote-itinerary">
              {itinerary(t).map((d) => (
                <li key={d.day} className="flex gap-3 text-sm"><span className="w-12 shrink-0 text-xs font-semibold leading-5 text-slate-400">Day {d.day}</span><span className="min-w-0 text-navy-900"><MapPin className="mr-1 inline h-3 w-3 text-slate-300" />{d.title}{d.place !== d.title && <span className="text-slate-400"> · {d.place}</span>}</span></li>
              ))}
            </ol>
          </div>
          <div className="space-y-5 lg:col-span-2">
            <div className="rounded-2xl bg-navy-900 p-5 text-white">
              <p className="text-xs text-white/60">Trip total</p>
              <p className="font-display text-3xl font-bold" data-testid="public-quote-total">{fmtMoney(tripTotal(t), true)}</p>
              <p className="mt-2 text-xs text-white/60">{fmtMoney(deposit, true)} deposit to confirm · {t.travelers?.length || 0} travelers · {t.nights} nights</p>
            </div>
            <Card className="shadow-none"><CardTitle>Payment plan</CardTitle><div className="p-5"><PaymentSchedule trip={t} /></div></Card>
          </div>
        </div>
        {t.status === "QUOTE" && <QuoteTerms />}
      </Card>

      {t.status === "QUOTE" ? (
        <div className="mx-auto mt-5 max-w-4xl px-4 print:hidden" data-testid="public-approve-area">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 text-center shadow-sm">
            <p className="text-sm text-slate-600">Happy with this quote? Approve it and your advisor will finalize your booking.</p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
              <button onClick={approve} disabled={approving} data-testid="public-approve-btn" className={`inline-flex h-11 items-center gap-2 rounded-full px-6 text-sm font-semibold text-white transition-colors disabled:opacity-60 ${confirm ? "bg-emerald-600 hover:bg-emerald-700" : "bg-navy-900 hover:bg-navy-800"}`}>
                {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {approving ? "Approving…" : confirm ? "Tap again to confirm" : "Approve this quote"}
              </button>
              {confirm && !approving && <button onClick={() => setConfirm(false)} data-testid="public-approve-cancel" className="inline-flex h-11 items-center rounded-full border border-slate-200 px-5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50">Cancel</button>}
            </div>
            <p className="mt-3 text-xs text-slate-400">This is a quote, not a confirmed booking until your advisor finalizes it.</p>
          </div>
        </div>
      ) : (
        <div className="mx-auto mt-5 max-w-4xl px-4 print:hidden" data-testid="public-approved-area">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600" />
            <p className="mt-2 font-display text-lg font-bold text-navy-900">Quote approved — thank you!</p>
            <p className="mt-1 text-sm text-slate-600">{t.bookingNumber ? `Your booking reference is ${t.bookingNumber}. ` : ""}Your advisor will finalize the details and reach out shortly.</p>
            <a href="/launch" data-testid="public-open-mytrip" className="mt-4 inline-flex h-11 items-center gap-2 rounded-full bg-navy-900 px-6 text-sm font-semibold text-white transition-colors hover:bg-navy-800">Open MyTrip</a>
          </div>
        </div>
      )}
    </div>
  );
}
